import { useLocalization } from '@im/react-localization';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { useIsAuthenticated } from 'mf_core/auth';
import { useFeatureFlagBase } from '../hooks/use-feature-flag-base';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const LOCAL_STORAGE_KEY = 'isPerformanceAlertDismissed';

export function PerformanceErrorAlert() {
  const { translate } = useLocalization();
  const { pathname } = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const [isDismissed, setIsDismissed] = useState(
    localStorage.getItem(LOCAL_STORAGE_KEY) === 'true'
  );

  const dismissAlert = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, 'true');
    setIsDismissed(true);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    }
  }, [isAuthenticated]);

  const hideOnRoutes = [
    '/my/checkout',
    '/my/quotemgmt/quotelist',
    '/frame',
    '/my/invoice/invoicessearch',
  ];

  const boKeys = {
    EnablePerformanceBanner: 'X4C_Enable_Performance_Banner',
    EndTime: 'X4C_Performance_Banner_End_Time',
    StartTime: 'X4C_Performance_Banner_Start_Time',
  };

  const { boValues: boValue, isReady } = useFeatureFlagBase();

  let showAlert =
    !isDismissed &&
    isAuthenticated &&
    hideOnRoutes.find((route) => pathname.startsWith(route)) === undefined &&
    isReady &&
    boValue[boKeys.EnablePerformanceBanner] === 'true';

  if (showAlert && boValue[boKeys.StartTime] && boValue[boKeys.EndTime]) {
    try {
      const currentUtcDateTime = new Date().toISOString();
      const currentDate = currentUtcDateTime.substring(0, 10);
      const currentTime = new Date(
        `${currentDate} ${currentUtcDateTime.substring(11, 19)}`
      );
      const startTime = new Date(`${currentDate} ${boValue[boKeys.StartTime]}`);
      const endTime = new Date(`${currentDate} ${boValue[boKeys.EndTime]}`);
      if (startTime > currentTime || endTime < currentTime) showAlert = false;
    } catch {
      console.error('Performance Alert: Error calculating start/end time');
    }
  }

  useEffect(() => {
    if (showAlert)
      setTimeout(() => {
        document
          .querySelector('#main-view')
          ?.scrollTo({ behavior: 'smooth', top: 0 });
      }, 200);
  }, [pathname, showAlert]);

  return (
    <>
      {showAlert && (
        <StyledAlert
          action={
            <StyledButton onClick={dismissAlert} variant="text">
              {translate('Common.Dismiss')}
            </StyledButton>
          }
          icon={false}
          severity="warning"
        >
          {translate('Common.PerformanceError')}
        </StyledAlert>
      )}
    </>
  );
}

const StyledAlert = styled(Alert)(({ theme }) => ({
  '& .MuiAlert-action': {
    alignItems: 'center',
    padding: 0,
  },
  '& .MuiAlert-message': {
    color: `${theme.palette.warning[900]} !important`,
    fontSize: '18px',
    fontWeight: 400,
    lineHeight: '26px',
  },
  background: `${theme.palette.warning[100]} !important`,
  margin: '16px 32px 0 32px',
  position: 'relative',
  zIndex: 100,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  '&:focus': {
    outline: 'none',
  },
  '&:hover': {
    background: 'unset',
  },
  color: `${theme.palette.warning[900]}`,
  fontSize: '18px',
  fontWeight: 400,
  lineHeight: '26px',
  marginLeft: '100px',
  textTransform: 'none',
}));
